import type { SearchPagination, SearchResponse } from '@algolia/client-search';
import { Fragment } from 'react';

import { CompensationSurveyAd } from 'components/Ads/CompensationSurveyAd';
import { Pagination } from 'components/Pagination/Pagination';
import { trackSearchResultHitClick } from 'modules/listing/utils/trackSearchResultHitClick';
import type { SearchAlgoliaHit } from 'modules/search/algolia/types/SearchAlgoliaHit';
import { SearchHit } from 'modules/search/components/Hit/SearchHit';
import { SearchLoadingSectionMockResults } from 'modules/search/components/Loading/SearchLoadingSectionMockResults';
import { SaveSearchButton } from 'modules/search/components/SaveSearch/SaveSearchButton';
import type { SearchLocation } from 'modules/search/types/SearchLocation';
import type { SearchType } from 'modules/search/types/SearchType';
import { AdSection } from 'types/Ads/AdSection';
import { AdType } from 'types/Ads/AdType';
import { useAdType } from 'zustand-stores/adStore';

import { SearchResultsFooter } from './SearchResultsHits.styled';
import { SearchResultsHitsEmpty } from './SearchResultsHitsEmpty';
import { SearchResultsHitsHolder } from './SearchResultsHitsHolder';

type Props = {
  results?: SearchResponse<SearchAlgoliaHit> & Required<SearchPagination>;
  isSearching: boolean;
  searchType: SearchType;
  searchLocation: SearchLocation | undefined;
  getSearchTrackingData: () => Record<string, unknown>;
  saveSearch: () => void;
  onChangePageIndex: (pageIndex: number) => void;
};

export function SearchResultsHits({
  results,
  isSearching,
  searchType,
  searchLocation,
  getSearchTrackingData,
  saveSearch,
  onChangePageIndex,
}: Props) {
  const adType = useAdType(AdSection.INTERSTITIAL);

  if (!results || isSearching) return <SearchLoadingSectionMockResults />;

  const { hits, hitsPerPage, page, nbHits, nbPages, query } = results;

  if (results.hits.length === 0) {
    return (
      <SearchResultsHitsEmpty
        searchType={searchType}
        query={query}
        searchLocation={searchLocation}
      />
    );
  }

  return (
    <>
      <SearchResultsHitsHolder>
        {hits.map((hit, index) => (
          <Fragment key={hit.objectID}>
            <SearchHit
              hit={hit}
              trackClick={() =>
                trackSearchResultHitClick({
                  results,
                  hit,
                  index,
                  trackingData: getSearchTrackingData(),
                })
              }
            />

            {((hits.length <= 15 && index === hits.length - 1) ||
              index === 4 ||
              (hits.length === 20 && index === 14)) &&
              adType === AdType.SALARY_SURVEY && <CompensationSurveyAd />}
          </Fragment>
        ))}
      </SearchResultsHitsHolder>

      <SearchResultsFooter>
        {hits.length >= 5 && <SaveSearchButton saveSearch={saveSearch} />}

        <Pagination
          nbHits={nbHits}
          nbPages={nbPages}
          pageIndex={page}
          onChangePageIndex={onChangePageIndex}
          hitsPerPage={hitsPerPage}
        />
      </SearchResultsFooter>
    </>
  );
}
